/**
 * Named gesture-tuning presets.
 *
 * Centralising the recognizer tuning (deadzone, fling) together with the
 * consumer-facing layout constants (commit thresholds, rubber-band limits,
 * animation durations) keeps the feel consistent across every gesture surface
 * and makes tuning a one-line change. The values mirror the canon so MealPlan's
 * gestures feel identical to the rest of the product family.
 */

import type { FlingConfig } from "./types";

const GESTURE_DEADZONE_PX = 8;

const STANDARD_FLING: FlingConfig = {
  maxDurationMs: 300,
  minDistancePx: 50,
  axisRatio: 2,
};

/**
 * Per-row swipe-to-delete (left only) reused by the pantry and recipe lists.
 * Shares the shopping row physics so every deletable row feels identical; the
 * single committed direction is enforced by the consuming hook.
 */
export const ROW_DELETE_SWIPE = {
  thresholdPx: GESTURE_DEADZONE_PX,
  commitThresholdPx: 100,
  rubberBandLimitPx: 130,
  rubberBandFactor: 0.25,
  hapticMs: 15,
  commitAnimationMs: 300,
  cancelAnimationMs: 240,
  fallbackWidthPx: 400,
} as const;

/** Per-row swipe-to-action (e.g. mark bought / delete) on the shopping list. */
export const SHOPPING_ROW_SWIPE = {
  thresholdPx: GESTURE_DEADZONE_PX,
  commitThresholdPx: 100,
  rubberBandLimitPx: 130,
  rubberBandFactor: 0.25,
  hapticMs: 15,
  commitAnimationMs: 300,
  cancelAnimationMs: 240,
  fallbackWidthPx: 400,
} as const;

/**
 * Toast swipe-to-dismiss (either direction). Mirrors the row-swipe physics so a
 * flick on a toast feels identical to a flick on a list row; the commit fades
 * and flings the toast off its locked axis before removal.
 */
export const TOAST_SWIPE = {
  thresholdPx: GESTURE_DEADZONE_PX,
  commitThresholdPx: 80,
  rubberBandLimitPx: 130,
  rubberBandFactor: 0.4,
  hapticMs: 15,
  commitAnimationMs: 220,
  cancelAnimationMs: 200,
  fallbackWidthPx: 400,
} as const;

/** Recipe detail: horizontal swipe to page between recipes (carousel slide). */
export const RECIPE_SWIPE = {
  thresholdPx: GESTURE_DEADZONE_PX,
  commitThresholdPx: 64,
  commitThresholdRatio: 0.22,
  transitionMs: 240,
  fallbackWidthPx: 420,
  hapticMs: 12,
  fling: { ...STANDARD_FLING },
} as const;

/** Planner: horizontal swipe to page between weeks (carousel slide). */
export const WEEK_SWIPE = {
  thresholdPx: GESTURE_DEADZONE_PX,
  /** Commit at the smaller of a fixed travel or a fraction of the grid width. */
  commitThresholdPx: 56,
  commitThresholdRatio: 0.22,
  transitionMs: 240,
  fallbackWidthPx: 360,
  hapticMs: 12,
  fling: { ...STANDARD_FLING },
} as const;

/**
 * Mobile navigation drawer: drag it toward its own edge to dismiss it.
 *
 * The drawer is anchored to the left, so only a leftward drag means anything.
 * Dragging the other way has nothing to reveal — it gets a short, capped
 * rubber-band so the panel acknowledges the finger without ever detaching from
 * its edge or committing anything.
 */
export const NAV_DRAWER_DISMISS = {
  thresholdPx: GESTURE_DEADZONE_PX,
  /** Travel toward the edge that commits the close on release.*/
  commitThresholdPx: 72,
  /** Give on the closed-direction drag (the one that does nothing).*/
  rubberBandFactor: 0.25,
  /** Hard cap on that give.*/
  rubberBandLimitPx: 24,
  hapticMs: 12,
  /** Used for the backdrop fade when the panel has not been measured yet.*/
  fallbackWidthPx: 280,
  /** A flick dismisses without reaching `commitThresholdPx`.*/
  fling: { maxDurationMs: 300, minDistancePx: 40, axisRatio: 2 } satisfies FlingConfig,
} as const;
